// src/components/antenna-history.tsx
import { useEffect, useState, useMemo } from "react";
import { X, Search, Loader2, ChevronRight, AlertCircle } from "lucide-react";
import { apiFetch } from "@/api/api-fetch";

interface HistoryItem {
  id: number;
  brandName: string;
  createdAt: string | null;
  status: string;
}

interface AntennaHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (runId: number) => void;
  selectingId: number | null; // ID currently being loaded by parent
}

function formatWhen(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AntennaHistory({ isOpen, onClose, onSelect, selectingId }: AntennaHistoryProps) {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    
    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await apiFetch<{ items: HistoryItem[] }>("/monitoring/history?limit=50");
        if (!cancelled) setItems(resp.items || []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load history");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchHistory();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const lowerQ = searchQuery.toLowerCase();
    return items.filter((item) => 
      item.brandName.toLowerCase().includes(lowerQ)
    );
  }, [items, searchQuery]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[90]"
        style={{ backgroundColor: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" }}
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div
        className="fixed top-0 right-0 h-full w-[420px] max-w-[92vw] z-[100] border-l flex flex-col"
        style={{
          backgroundColor: "var(--card)",
          borderColor: "var(--border)",
          boxShadow: "var(--elevation-lg)",
          animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: "var(--border)" }}>
          <div>
            <p style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
              Brand Search History
            </p>
            <p className="text-muted-foreground" style={{ fontSize: "var(--text-sm)" }}>
              Load results from previous analyses
            </p>
          </div>
          <button
            className="p-2 rounded-lg hover:opacity-90 transition-opacity"
            style={{ border: "1px solid var(--border)" }}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b flex-shrink-0" style={{ borderColor: "var(--border)", backgroundColor: "var(--muted-5)" }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input 
              type="text"
              placeholder="Search past brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-all"
              style={{ 
                backgroundColor: "var(--background)",
                borderColor: "var(--border)",
                ["--tw-ring-color" as any]: "var(--accent-20)"
              }}
            />
          </div>
        </div>

        {/* Content List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-3">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Loading history...</span>
            </div>
          )}

          {error && (
            <div
              className="rounded-lg p-4 flex gap-3 items-start"
              style={{
                backgroundColor: "var(--infrared-coral-10)",
                border: "1px solid var(--infrared-coral-30)",
                color: "var(--infrared-coral)",
              }}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <div className="text-center py-10 text-muted-foreground">
              <p>No previous runs found.</p>
            </div>
          )}

          {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
            <div className="text-center py-10 text-muted-foreground">
              <p>No results match "{searchQuery}"</p>
            </div>
          )}

          <div className="space-y-2">
            {filteredItems.map((it) => (
              <button
                key={it.id}
                onClick={() => onSelect(it.id)}
                disabled={selectingId === it.id}
                className="w-full text-left rounded-xl p-4 border transition-all hover:border-accent group relative overflow-hidden"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--background)",
                  opacity: selectingId === it.id ? 0.6 : 1,
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p style={{ fontWeight: "var(--font-weight-semibold)" }} className="truncate mb-1 group-hover:text-accent transition-colors">
                      {it.brandName}
                    </p>
                    <p className="text-muted-foreground" style={{ fontSize: "var(--text-sm)" }}>
                      {formatWhen(it.createdAt)}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                     {selectingId === it.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-accent" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                      )}
                      <span 
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                        style={{ 
                            color: it.status === 'complete' ? 'var(--signal-blue)' : 'var(--muted-foreground)',
                            borderColor: "var(--border)",
                            backgroundColor: "var(--card)"
                        }}
                      >
                        {it.status}
                      </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}